import { useRef, useEffect, useMemo } from 'react';

import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { selectClipData, calculateTimePosition } from 'components/app/_helpers/_app.helper';
import { EnumDragger } from 'components/app/_mediator/app.slice';
import { useDraggerContext } from 'components/app/_mediator/app-dragger.provider';
import { useEditorCb } from 'components/app/editor/_mediator/use-editor-cb';
import { useClipsTimestamp } from 'components/app/editor/_mediator/editor-clips/use-clips-timestamp';

import { setTimeBoundaryClip } from './_mediator/timestamp.slice';
import { getTsSliderActions, getTsSliderInitData, MIN_STEP } from './_mediator/timestamp.helper';

const VAR__POS_START = '--position-start';
const VAR__POS_END = '--position-end';

export type Action = 'start' | 'end';

interface HandlesData {
  start: Seconds;
  end: Seconds;
  min: Seconds;
  max: Seconds;
}

export const useTsSlider = () => {
  const { getPlayerRef } = useEditorCb();
  const { dragger, isDragging } = useDraggerContext();
  const {
    currentPlayerDuration: playerDuration,
    hotkeyPlayerTimeListener,
    tsLinkedListener,
    tsLinkedPayload,
  } = useAppSelector((state) => state.timestamp);
  const appDragger = useAppSelector((state) => state.app.dragger);
  const beginnerMode = useAppSelector((state) => state.app.beginnerMode);
  const activeClip = useAppSelector((state) => state.editor.activeClip);
  const clipsMap = useAppSelector((state) => state.editor.clipsMap);
  const dispatch = useAppDispatch();

  const container = useRef<HTMLDivElement>(null);
  const startHandle = useRef<HTMLDivElement>(null);
  const endHandle = useRef<HTMLDivElement>(null);
  const draggerEnd = useRef<() => void>();

  const initData = useRef<HandlesData>();
  const isStart = useRef(true);

  const handleTimestamp = useClipsTimestamp(activeClip ?? undefined);

  const setHandlePosition = (time: Seconds, action: Action) => {
    const elem = action === 'start' ? startHandle : endHandle;
    const prop = action === 'start' ? VAR__POS_START : VAR__POS_END;
    elem.current && elem.current.style.setProperty(prop, calculateTimePosition(time, playerDuration));
  };

  const setHandles = (data: HandlesData) => {
    setHandlePosition(data.start, 'start');
    setHandlePosition(data.end, 'end');
  };

  const onDrag = (event: React.PointerEvent, action: Action) => {
    isStart.current = action === 'start';

    const player = getPlayerRef();
    const timeFrom = player?.getCurrentTime();
    const handleEl = isStart.current ? startHandle : endHandle;
    const handleRect = handleEl.current!.getBoundingClientRect();
    const containerRect = container.current!.getBoundingClientRect();
    const offset = event.clientX - (handleRect.left + handleRect.width / 2);
    const range = isStart.current
      ? {
          min: initData.current!.min,
          max: initData.current!.end - MIN_STEP,
        }
      : {
          min: initData.current!.start + MIN_STEP,
          max: initData.current!.max,
        };
    //

    dragger(handleEl, EnumDragger.tsSlider).start(event, (event) => {
      const time = ((event.clientX - containerRect.left - offset) / containerRect.width) * playerDuration;
      if (tsLinkedPayload) {
        const { boundaryLeft, boundaryRight } = tsLinkedPayload;
        dispatch(
          setTimeBoundaryClip(
            isStart.current && time < range.min
              ? boundaryLeft
              : !isStart.current && time > range.max
              ? boundaryRight
              : null
            //
          )
        );
      }
      const res = time < range.min ? range.min : time > range.max ? range.max : time;
      setHandlePosition(res, action);
      player!.seekTo(res, 'seconds');
    });

    draggerEnd.current = () => {
      dispatch(setTimeBoundaryClip(null));
      if (Number(timeFrom).toFixed(2) === Number(player?.getCurrentTime()).toFixed(2)) return;
      const res = handleTimestamp(action);
      if (res?.reset) {
        setHandles(initData.current!);
        player!.seekTo(timeFrom!, 'seconds');
      }
    };
  };

  const onDragEnd = (event: React.PointerEvent, action: Action) => {
    const elem = action === 'start' ? startHandle : endHandle;
    dragger(elem, EnumDragger.tsSlider).end(event, draggerEnd.current!);
  };

  const actions = useMemo(() => {
    if (isDragging.current) return;
    if (!activeClip || !clipsMap) return;
    const clip = selectClipData(activeClip, clipsMap);
    initData.current = getTsSliderInitData(clip, tsLinkedPayload, playerDuration);
    setHandles(initData.current);
    return getTsSliderActions(clip, tsLinkedPayload, playerDuration, beginnerMode);
  }, [tsLinkedListener, beginnerMode]);

  useEffect(() => {
    if (appDragger !== EnumDragger.tsSlider) return;
    setHandlePosition(hotkeyPlayerTimeListener!, isStart.current ? 'start' : 'end');
  }, [hotkeyPlayerTimeListener]);

  return { container, startHandle, endHandle, actions, onDrag, onDragEnd };
};
